/* Real, category-matched catalogue previews. Category links, prices and stock stay unchanged. */
(() => {
  const INTERVAL = 5000;
  const chooseNext = (items, previous, random = Math.random) => {
    const stocked = items.filter((item) => item.stockListed === true);
    const pool = stocked.length ? stocked : items;
    const different = pool.filter((item) => item.url !== previous);
    const choices = different.length ? different : pool;
    return choices.length ? choices[Math.floor(random() * choices.length)] : null;
  };
  function safeImage(value, origin) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, origin);
      return url.protocol === 'https:' && !url.username && !url.password &&
        (url.origin === origin || url.hostname === 'cdn.shopify.com') ? url.href : null;
    } catch { return null; }
  }
  function feedURL(value, origin) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, origin);
      if (url.origin !== origin || url.username || url.password) return null;
      const collection = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[a-z0-9][a-z0-9-]*(?:\/[a-z0-9+%_-]+)?\/?$/i.test(url.pathname);
      const search = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(url.pathname) && url.searchParams.get('q');
      if (!collection && !search) return null;
      const query = search ? url.searchParams.get('q') : null;
      url.search = ''; url.hash = '';
      if (search) { url.searchParams.set('q', query); url.searchParams.set('type', 'product'); }
      url.searchParams.set('view', 'category-media');
      return url.pathname + url.search;
    } catch { return null; }
  }
  function validItems(data, origin) {
    if (!Array.isArray(data)) return [];
    const seen = new Set();
    return data.slice(0, 50).flatMap((item) => {
      if (!item || typeof item.title !== 'string' || !item.title.trim() || item.title.length > 500 ||
          typeof item.url !== 'string' || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9_-]*\/?$/i.test(item.url)) return [];
      const image = safeImage(item.image, origin);
      if (!image || seen.has(item.url)) return [];
      seen.add(item.url);
      return [{ title: item.title, url: item.url, image, stockListed: item.stockListed === true }];
    });
  }
  function nextPreview(items, current, failed = new Set()) {
    const pool = items.filter((item) => !failed.has(item.image));
    if (!pool.length) return null;
    const start = pool.findIndex((item) => item.url === current?.url);
    for (let offset = 1; offset <= pool.length; offset += 1) {
      const item = pool[(start + offset) % pool.length];
      if (item.url !== current?.url && item.image !== current?.image) return item;
    }
    return null;
  }
  function createFeedCache(fetcher, origin, timers = globalThis) {
    const cache = new Map();
    return (value) => {
      const url = feedURL(value, origin);
      if (!url) return Promise.resolve([]);
      if (!cache.has(url) && cache.size >= 64) return Promise.resolve([]);
      if (!cache.has(url)) {
        const controller = new AbortController();
        const timeout = timers.setTimeout(() => controller.abort(), 8000);
        const request = Promise.resolve().then(() => fetcher(url, { credentials: 'same-origin', signal: controller.signal }))
          .then((response) => response.ok ? response.json() : [])
          .then((data) => validItems(data, origin)).catch(() => [])
          .finally(() => timers.clearTimeout(timeout));
        cache.set(url, request);
      }
      return cache.get(url);
    };
  }
  function loadPreviewImage(item, windowObject) {
    return new Promise((resolve) => {
      const image = new windowObject.Image();
      let settled = false;
      const finish = (result) => {
        if (settled) return;
        settled = true; windowObject.clearTimeout(timeout);
        image.onload = null; image.onerror = null; resolve(result);
      };
      const timeout = windowObject.setTimeout(() => finish(null), 8000);
      image.width = 900; image.height = 600; image.alt = item.title; image.decoding = 'async';
      image.onload = () => finish(image.naturalWidth > 0 ? image : null);
      image.onerror = () => finish(null);
      image.src = item.image;
    });
  }
  function createZoneController(zone, environment) {
    const { document: doc, window: win, getFeed, loadImage = (item) => loadPreviewImage(item, win) } = environment;
    const button = zone.querySelector('[data-category-rotation-toggle]');
    const status = zone.querySelector('[data-category-rotation-status]');
    const cards = [...zone.querySelectorAll('[data-category-showcase]')].map((card) => ({
      card, items: [], failed: new Set(), current: null, generation: 0, initialized: false, pending: false,
      sources: (card.dataset.categoryFeedUrls || card.getAttribute('href') || '').split('|').filter(Boolean).slice(0, 6)
    }));
    let disposed = false, ready = false, loading = false, manualPause = false, hover = false, focused = false;
    let visible = !win.IntersectionObserver, timer = null;
    const motion = win.matchMedia ? win.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
    const canDisplay = () => !disposed && ready && visible && !doc.hidden && !manualPause && !hover && !focused;
    const canRun = () => canDisplay() && !motion.matches && Boolean(win.IntersectionObserver);
    const hasRotation = () => cards.some((state) => new Set(state.items.map((item) => item.image)).size > 1);
    function updateControls() {
      if (button) {
        button.hidden = !ready || !hasRotation();
        button.disabled = Boolean(motion.matches) || !win.IntersectionObserver;
        button.textContent = button.disabled ? 'Product rotation off' : manualPause ? 'Resume product rotation' : 'Pause product rotation';
      }
      if (status && ready) status.textContent = motion.matches ? 'Automatic rotation is off for reduced motion' : !win.IntersectionObserver || !hasRotation() ? 'Browse the current ranges' : manualPause ? 'Product rotation paused' : 'Product previews change every 5 seconds';
    }
    function stop() {
      if (timer !== null) win.clearTimeout(timer);
      timer = null;
      cards.forEach((state) => { state.generation += 1; });
    }
    function schedule() {
      if (timer === null && canRun() && hasRotation() && cards.every((state) => state.initialized && !state.pending)) timer = win.setTimeout(tick, INTERVAL);
    }
    function ensureInitial() {
      if (!canDisplay()) return;
      cards.filter((state) => !state.initialized && !state.pending).forEach((state) => {
        state.pending = true;
        swap(state, true).then((completed) => {
          state.pending = false;
          state.initialized = completed;
          if (!completed && canDisplay()) ensureInitial();
          schedule();
        });
      });
    }
    function sync() { if (!canRun()) stop(); ensureInitial(); schedule(); updateControls(); }
    async function swap(state, initial = false) {
      const selected = nextPreview(state.items, state.current, state.failed);
      if (!selected) return true;
      const generation = ++state.generation;
      let image;
      try { image = await loadImage(selected); } catch { image = null; }
      if (!state.card.isConnected) return true;
      if (disposed || state.generation !== generation || !(initial ? canDisplay() : canRun())) return false;
      if (!image) { state.failed.add(selected.image); return true; }
      const media = state.card.querySelector('.category-card__media');
      const label = state.card.querySelector('[data-category-model]');
      if (!media || !label) return true;
      // Commit the loaded image and its title together. The card keeps its category href.
      media.replaceChildren(image);
      label.textContent = `Shown: ${selected.title}`; label.hidden = false;
      state.current = selected;
      state.card.dataset.categoryPreviewProduct = selected.url;
      return true;
    }
    async function tick() {
      timer = null;
      if (!canRun()) return;
      await Promise.all(cards.map((state) => swap(state)));
      schedule();
    }
    async function ensureLoaded() {
      if (loading || ready || disposed) return;
      loading = true;
      await Promise.all(cards.map(async (state) => {
        const feeds = await Promise.all(state.sources.map(getFeed));
        if (disposed) return;
        const seen = new Set();
        state.items = feeds.flat().filter((item) => {
          if (seen.has(item.url)) return false;
          seen.add(item.url); return true;
        });
        // Prefer a stock-listed first preview without preventing other real products rotating.
        state.items.sort((a, b) => Number(b.stockListed) - Number(a.stockListed));
      }));
      if (disposed) return;
      ready = true; sync();
    }
    const listeners = [];
    const listen = (target, event, handler) => {
      target.addEventListener(event, handler); listeners.push(() => target.removeEventListener(event, handler));
    };
    listen(zone, 'mouseenter', () => { hover = true; sync(); });
    listen(zone, 'mouseleave', () => { hover = false; sync(); });
    listen(zone, 'focusin', () => { focused = true; sync(); });
    listen(zone, 'focusout', (event) => { focused = zone.contains(event.relatedTarget); sync(); });
    listen(doc, 'visibilitychange', () => { if (!doc.hidden && visible) ensureLoaded(); sync(); });
    if (button) listen(button, 'click', () => { manualPause = !manualPause; sync(); });
    const motionChanged = () => sync();
    if (motion.addEventListener) listen(motion, 'change', motionChanged);
    else if (motion.addListener) { motion.addListener(motionChanged); listeners.push(() => motion.removeListener(motionChanged)); }
    const observer = win.IntersectionObserver ? new win.IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
      if (visible && !doc.hidden) ensureLoaded();
      sync();
    }, { threshold: 0.01 }) : null;
    if (observer) observer.observe(zone);
    else if (!doc.hidden) ensureLoaded();
    updateControls();
    return {
      destroy() {
        disposed = true; stop(); observer?.disconnect(); listeners.forEach((remove) => remove());
      }
    };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { chooseNext, safeImage, feedURL, validItems, nextPreview, createFeedCache, createZoneController };
  if (typeof document === 'undefined' || typeof window === 'undefined' || window.qt4uCategoryShowcase) return;
  const controllers = new Map();
  const getFeed = createFeedCache(window.fetch.bind(window), window.location.origin, window);
  const init = () => {
    document.querySelectorAll('[data-category-rotation-zone]').forEach((zone) => {
      if (!controllers.has(zone)) controllers.set(zone, createZoneController(zone, { document, window, getFeed }));
    });
  };
  window.qt4uCategoryShowcase = { init };
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller, zone) => {
      if (zone === event.target || event.target.contains(zone)) { controller.destroy(); controllers.delete(zone); }
    });
  });
  init();
})();
